/**
 * Advanced Multi-Pillar Procedural Artwork Generator for "The Caesura Engine"
 *
 * Implements a 24+ style visual taxonomy across 6 core aesthetic categories:
 *
 * I. COSMIC & VOID PHENOMENA
 *    - cosmic_flare: Blinding Planck ignition flash, planetary limb, twin moons, purple nebulae
 *    - event_horizon: Gravitational singularity, warped accretion disk, Doppler blue-shift
 *    - stellar_nursery: Luminous ionized filaments, turquoise gas dust, derelict hulls
 *    - unrendered_void: Infinitesimal Planck gap, uncalculated matrix, non-Euclidean stillness
 *
 * II. ATMOSPHERIC OUTPOSTS & MEGASTRUCTURES
 *    - cloud_station: Golden observation cupolas floating above turquoise cloud seas (gas giant)
 *    - orbital_gantry: Skeletal kilometer-long drydocks, magnetic cranes, welding flare
 *    - perseus_terminal: Sprawling orbital megalopolises, rotating habitat rings, holo-billboards
 *    - deep_quarantine: High mooring slip 14, ominous quarantine beacons, isolated observation
 *
 * III. SHIPS, FLEETS & INDUSTRIAL KEELS
 *    - solar_armada: Retro-futuristic hard-surface cargo fleets against blazing yellow solar spheres
 *    - solitary_hauler: The USSC Geryon drifting solitary across infinite stillness, 4km keel
 *    - derelict_graveyard: Fractured ship hulls, severed trusses frozen in vacuum, forgotten hulls
 *    - corporate_cutter: Sleek matte-black Thorne-Vance Inquest vessels, scanning sensor arrays
 *
 * IV. CLAUSTROPHOBIC INTERIORS & WELLS
 *    - phosphor_spine: Grimy lower engineering wells, toxic phosphor-green volumetric lighting
 *    - cryostat_choke: Liquid helium pipes at 1.8K, crawling frost, stainless steel manifolds
 *    - stasis_vaults: Ranks of frosted hibernation pods, blue LED monitors, silent sleeping crews
 *    - cargo_void: Cavernous unpressurized cargo vaults, magnetic clamps, pitch-black shadows
 *    - quantum_schematic: Holographic Mark IX Decoupler Core blueprints, niobium magnetic rings
 *
 * V. ARCHITECTURE, TRANSIT & TRIBUNALS
 *    - transit_corridor: Sharp perspective vanishing points, overhead volumetric light beams, safety stencils
 *    - corporate_tribunal: Inquisitor debriefing rooms, vitrified black glass, harsh downlights
 *    - chrono_psych_ward: Convalescent medical recovery bays, retinal diagnostic monitors
 *
 * VI. EXISTENTIAL HORROR & COSMIC LEDGER
 *    - synaptic_tear: Biological neural webs tearing with chromatic aberration (crimson/cyan)
 *    - spectators_margin: Patient geometric silhouettes standing in the unrendered clock-tick pause
 *    - cosmic_ledger: Monolithic obsidian tablets with glowing golden coordinate equations
 *    - recrystallization_shear: Explosive physical re-crystallization light-years away, atomic shock
 */

import { ArtworkStyle, ArtworkCue } from '../types/tome';

export interface StyleMeta {
  style: ArtworkStyle;
  label: string;
  category: 'Cosmic & Void' | 'Stations & Megastructures' | 'Ships & Fleets' | 'Interiors & Wells' | 'Architecture & Transit' | 'Existential Horror';
  accentColor: string;
  secondaryColor: string;
  glowRgba: string;
  heroImage?: string;
  prompt: string;
  captionTemplate: string;
}

export const STYLE_REGISTRY: Record<ArtworkStyle, StyleMeta> = {
  // --- I. Cosmic & Void ---
  cosmic_flare: {
    style: 'cosmic_flare',
    label: 'Cosmic Flare Singularity',
    category: 'Cosmic & Void',
    accentColor: '#e040fb',
    secondaryColor: '#00f3ff',
    glowRgba: 'rgba(224, 64, 251, 0.28)',
    heroImage: '/art/hero_cosmic_flare.jpg',
    prompt: 'Vast cosmic planetary orbital gantry, blinding Planck ignition solar flash across twin moons and purple nebula, intricate station spires, 8k dark sci-fi masterpiece.',
    captionTemplate: 'PLANCK IGNITION // SOLAR BURST CONVERGENCE // SECTOR 0x00-THETA',
  },
  event_horizon: {
    style: 'event_horizon',
    label: 'Event Horizon Abyss',
    category: 'Cosmic & Void',
    accentColor: '#7c4dff',
    secondaryColor: '#ff2a4b',
    glowRgba: 'rgba(124, 77, 255, 0.25)',
    prompt: 'Gravitational singularity in deep space, warped relativistic accretion disk, Doppler blue-shift, pitch-black event horizon tearing spacetime fabric, 8k cinematic.',
    captionTemplate: 'GRAVITATIONAL ACCRETION // DOPPLER SHIFT -0.44c // NULL RIFT',
  },
  stellar_nursery: {
    style: 'stellar_nursery',
    label: 'Stellar Nursery Filaments',
    category: 'Cosmic & Void',
    accentColor: '#00e5ff',
    secondaryColor: '#76ff03',
    glowRgba: 'rgba(0, 229, 255, 0.25)',
    prompt: 'Ionized gas filaments of a deep stellar nursery, glowing turquoise and emerald cosmic dust curtains illuminating derelict industrial keels, volumetric lighting, 8k.',
    captionTemplate: 'IONIZED GAS FILAMENTS // TURQUOISE NURSERY // DUST OPACITY: 88%',
  },
  unrendered_void: {
    style: 'unrendered_void',
    label: 'The Unrendered Gap',
    category: 'Cosmic & Void',
    accentColor: '#00f3ff',
    secondaryColor: '#ffffff',
    glowRgba: 'rgba(0, 243, 255, 0.2)',
    prompt: 'Infinitesimal pause between two Planck moments, cold white coordinate lattice grid floating in timeless stillness, pitch black non-space where reality halts, 8k.',
    captionTemplate: 'UNRENDERED INTERVAL // CHRONO 5.39E-44s // ABSOLUTE ZERO VELOCITY',
  },
  vast_spaces: {
    style: 'vast_spaces',
    label: 'Deep Void Horizon',
    category: 'Cosmic & Void',
    accentColor: '#00f3ff',
    secondaryColor: '#9d4edd',
    glowRgba: 'rgba(0, 243, 255, 0.22)',
    heroImage: '/art/hero_cosmic_flare.jpg',
    prompt: 'Cinematic hyper-detailed dark sci-fi concept art of a colossal titanium starship keel drifting through an infinitesimal unrendered gap in spacetime, 8k.',
    captionTemplate: 'INFINITESIMAL COORDINATE GAP // PERSEUS DEEP SECTOR',
  },
  cosmic_void: {
    style: 'cosmic_void',
    label: 'Cosmic Void Continuum',
    category: 'Cosmic & Void',
    accentColor: '#00f3ff',
    secondaryColor: '#536dfe',
    glowRgba: 'rgba(83, 109, 254, 0.25)',
    heroImage: '/art/hero_cosmic_flare.jpg',
    prompt: 'Deep interstellar void, pinpoint starfield, subtle cosmic dust filament, vast lonely space vacuum.',
    captionTemplate: 'VACUUM DENSITY: ZERO // CONTINUUM CHECK: PASS',
  },

  // --- II. Stations & Megastructures ---
  cloud_station: {
    style: 'cloud_station',
    label: 'Jovian Cloud Aerostat',
    category: 'Stations & Megastructures',
    accentColor: '#ffb700',
    secondaryColor: '#00e5ff',
    glowRgba: 'rgba(255, 183, 0, 0.28)',
    heroImage: '/art/hero_cloud_station.png',
    prompt: 'Painterly floating sky station with glowing golden rounded observation domes, serene turquoise cloud layers, giant gas giant moon in twilight, Syd Mead concept art.',
    captionTemplate: 'JOVIAN CLOUD REFINERY // GOLDEN OBSERVATION DOMES // ALTITUDE 42km',
  },
  orbital_gantry: {
    style: 'orbital_gantry',
    label: 'Orbital Construction Gantry',
    category: 'Stations & Megastructures',
    accentColor: '#ff9100',
    secondaryColor: '#00f3ff',
    glowRgba: 'rgba(255, 145, 0, 0.25)',
    prompt: 'Kilometer-long skeletal orbital drydock gantry, magnetic crane arms, brilliant blue electric welding flares against dark vacuum, modular starship under construction, 8k.',
    captionTemplate: 'ORBITAL DRYDOCK 09 // CRANE VECTOR 4B // ASSEMBLY PROGRESS 74%',
  },
  perseus_terminal: {
    style: 'perseus_terminal',
    label: 'Perseus-Theta Megalith',
    category: 'Stations & Megastructures',
    accentColor: '#00e676',
    secondaryColor: '#ffab00',
    glowRgba: 'rgba(0, 230, 118, 0.25)',
    prompt: 'Massive rotating orbital habitat city, illuminated window rings, automated cargo shuttles with engine burns, corporate spires, volumetric atmospheric dust, 8k.',
    captionTemplate: 'PERSEUS-THETA HIGH ORBIT // TRAFFIC FREQUENCY 1400/HR // COMBINE COMMERCE',
  },
  deep_quarantine: {
    style: 'deep_quarantine',
    label: 'High Quarantine Mooring',
    category: 'Stations & Megastructures',
    accentColor: '#ff1744',
    secondaryColor: '#ffd600',
    glowRgba: 'rgba(255, 23, 68, 0.28)',
    prompt: 'High mooring slip 14 quarantine station, ominous rotating red warning beacons, sealed decontamination clamps holding scarred bulk hauler, isolated deep space post.',
    captionTemplate: 'QUARANTINE MOORING SLIP 14 // BIO-CHRONO HAZARD LEVEL 4 // SEALED DOCK',
  },
  busy_stations: {
    style: 'busy_stations',
    label: 'Commercial Transit Hub',
    category: 'Stations & Megastructures',
    accentColor: '#ffb700',
    secondaryColor: '#00f3ff',
    glowRgba: 'rgba(255, 183, 0, 0.25)',
    heroImage: '/art/hero_cloud_station.png',
    prompt: 'Bustling space station docking terminal with automated freight shuttles, amber guidance lasers, industrial gantries, dark space backdrop, 8k.',
    captionTemplate: 'STATION TRANSIT MATRIX // AUTOMATED BERTHS ACTIVE',
  },

  // --- III. Ships & Fleets ---
  solar_armada: {
    style: 'solar_armada',
    label: 'Solar Sphere Armada',
    category: 'Ships & Fleets',
    accentColor: '#ffd600',
    secondaryColor: '#ff6d00',
    glowRgba: 'rgba(255, 214, 0, 0.3)',
    heroImage: '/art/hero_solar_fleet.png',
    prompt: 'Retro-futuristic hard sci-fi cargo fleet in deep space against a massive glowing yellow solar sphere, hard-surface titanium hulls, glowing yellow solar sensor arrays, Syd Mead.',
    captionTemplate: 'COMBINE CONVOY // SOLAR ALIGNMENT // HULL SENSORS AT 100% EXPOSURE',
  },
  solitary_hauler: {
    style: 'solitary_hauler',
    label: 'USSC Geryon Deep Drift',
    category: 'Ships & Fleets',
    accentColor: '#00f3ff',
    secondaryColor: '#ffb700',
    glowRgba: 'rgba(0, 243, 255, 0.22)',
    prompt: 'Four-kilometer bulk carrier USSC Geryon drifting alone in immense silent space gulf, glowing cyan ion drive exhaust, frost-covered cryo tanks, titanium armor plating, 8k.',
    captionTemplate: 'USSC GERYON // 4.12km KEEL // LITHIUM BULK TRANSPORT // INERTIAL DRIFT',
  },
  derelict_graveyard: {
    style: 'derelict_graveyard',
    label: 'Void Derelict Graveyard',
    category: 'Ships & Fleets',
    accentColor: '#607d8b',
    secondaryColor: '#ff5252',
    glowRgba: 'rgba(96, 125, 139, 0.25)',
    prompt: 'Graveyard of shattered starships drifting silently in dead space, fractured spine trusses, floating cargo containers frozen in vacuum, faint warning beacon dying, 8k.',
    captionTemplate: 'DERELICT CONCORDANCE // 412 CASUALTIES CATALOGED // SALVAGE PRIORITY: LOW',
  },
  corporate_cutter: {
    style: 'corporate_cutter',
    label: 'Thorne-Vance Inquest Cutter',
    category: 'Ships & Fleets',
    accentColor: '#d500f9',
    secondaryColor: '#00e5ff',
    glowRgba: 'rgba(213, 0, 249, 0.25)',
    prompt: 'Predatory matte-black corporate enforcement vessel, angular stealth geometry, sweeping sensor dishes, cold blue interior cockpit glow, corporate seal, 8k.',
    captionTemplate: 'COMBINE INQUEST SQUADRON // BLACK BOX SEIZURE WARRANT #882',
  },
  lonely_ships: {
    style: 'lonely_ships',
    label: 'Solitary Keel Trajectory',
    category: 'Ships & Fleets',
    accentColor: '#00f3ff',
    secondaryColor: '#ff9100',
    glowRgba: 'rgba(0, 243, 255, 0.25)',
    heroImage: '/art/hero_solar_fleet.png',
    prompt: 'Lonely titanium cargo hauler sailing across cold cosmic void, single glowing drive cone, pitch black vacuum.',
    captionTemplate: 'SINGLE VESSEL MANIFEST // ZERO SATELLITES WITHIN SCAN RANGE',
  },
  cryogenic_hull: {
    style: 'cryogenic_hull',
    label: 'Cryogenic Hull Catwalk',
    category: 'Ships & Fleets',
    accentColor: '#00e5ff',
    secondaryColor: '#ffab00',
    glowRgba: 'rgba(0, 229, 255, 0.22)',
    prompt: 'Unpressurized exterior catwalk along starship spine, frost-covered helium lines, amber warning lights, zero-gravity floating grease.',
    captionTemplate: 'SPINE CATWALK 4 // PRESSURE: 0.00 kPa // VACUUM EXPOSURE',
  },

  // --- IV. Interiors & Wells ---
  phosphor_spine: {
    style: 'phosphor_spine',
    label: 'Phosphor Reactor Spine',
    category: 'Interiors & Wells',
    accentColor: '#00e676',
    secondaryColor: '#1de9b6',
    glowRgba: 'rgba(0, 230, 118, 0.3)',
    heroImage: '/art/hero_reactor_spine.jpg',
    prompt: 'Grimy industrial sci-fi engineering well, toxic phosphor-green volumetric lighting, dripping liquid helium pipes, heavy metal deck grating, Alien/Dead Space aesthetic.',
    captionTemplate: 'SPINE SECTION 4 // PHOSPHOR ILLUMINATION // REACTION VAPORS 40Hz',
  },
  cryostat_choke: {
    style: 'cryostat_choke',
    label: 'Liquid Helium Cryostat Choke',
    category: 'Interiors & Wells',
    accentColor: '#00b0ff',
    secondaryColor: '#ffffff',
    glowRgba: 'rgba(0, 176, 255, 0.28)',
    prompt: 'Superconducting cryostat choke manifold at 1.8 Kelvin, thick white rime frost on stainless steel conduits, bubbling cryo gauges, icy cyan spotlights, 8k.',
    captionTemplate: 'PRIMARY CHOKE 1.8K // VACUUM JACKETED HE-4 CONDUIT // SCRAM ARM OK',
  },
  stasis_vaults: {
    style: 'stasis_vaults',
    label: 'Hibernation Stasis Crypt',
    category: 'Interiors & Wells',
    accentColor: '#2979ff',
    secondaryColor: '#00e5ff',
    glowRgba: 'rgba(41, 121, 255, 0.25)',
    prompt: 'Endless rows of frosted stasis hibernation capsules in starship vault, faint cyan vitals displays, rhythmic respiratory tubes, condensation fog, 8k.',
    captionTemplate: 'STASIS CRYPT 02 // CREW SEDATION: ENFORCED // NEURO-LATTICE DORMANT',
  },
  cargo_void: {
    style: 'cargo_void',
    label: 'Depressurized Cargo Bay 4',
    category: 'Interiors & Wells',
    accentColor: '#ffab00',
    secondaryColor: '#37474f',
    glowRgba: 'rgba(255, 171, 0, 0.2)',
    prompt: 'Colossal unpressurized cargo hold, kilometers of titanium ribbing disappearing into darkness, magnetic hold-down clamps, floating dust particles, 8k.',
    captionTemplate: 'CARGO VAULT 4 // 80,000t LITHIUM ORE // ZERO ATMOSPHERE',
  },
  quantum_schematic: {
    style: 'quantum_schematic',
    label: 'Mark IX Core Blueprint',
    category: 'Interiors & Wells',
    accentColor: '#00f3ff',
    secondaryColor: '#76ff03',
    glowRgba: 'rgba(0, 243, 255, 0.25)',
    heroImage: '/art/hero_reactor_spine.jpg',
    prompt: 'Intricate holographic blueprint schematic of Mark IX Resonant Decoupler Core, niobium coils in magnetic containment, technical callouts, phosphor green.',
    captionTemplate: 'DECOUPLER CORE BLUEPRINT // FIELD CURVATURE: -0.0018G // PARITY: OK',
  },

  // --- V. Architecture & Transit ---
  transit_corridor: {
    style: 'transit_corridor',
    label: 'Industrial Transit Spine',
    category: 'Architecture & Transit',
    accentColor: '#ff5722',
    secondaryColor: '#cfd8dc',
    glowRgba: 'rgba(255, 87, 34, 0.25)',
    heroImage: '/art/hero_transit_corridor.png',
    prompt: 'Architectural sci-fi corridor concept sketch, sharp perspective, overhead volumetric sunlight, industrial signage stencils, ghosted double-vision lines.',
    captionTemplate: 'TRANSIT SPINE CORRIDOR // COMBINE ARMORED TRUSS // VOLUMETRIC SHAFT',
  },
  corporate_tribunal: {
    style: 'corporate_tribunal',
    label: 'Thorne-Vance Tribunal Chamber',
    category: 'Architecture & Transit',
    accentColor: '#d50000',
    secondaryColor: '#e0e0e0',
    glowRgba: 'rgba(213, 0, 0, 0.25)',
    prompt: 'Brutalist corporate tribunal chamber, polished black obsidian conference table, cold sterile surgical downlights, holographic legal transcript projection, 8k.',
    captionTemplate: 'CORPORATE TRIBUNAL PERSEUS // INQUEST RECORDING // EVIDENCE CLASSIFIED',
  },
  chrono_psych_ward: {
    style: 'chrono_psych_ward',
    label: 'Convalescent Recovery Wing',
    category: 'Architecture & Transit',
    accentColor: '#00b0ff',
    secondaryColor: '#ff4081',
    glowRgba: 'rgba(0, 176, 255, 0.22)',
    heroImage: '/art/hero_transit_corridor.png',
    prompt: 'Clinical quarantine medical ward, reinforced observation window overlooking distant star field, diagnostic retinal monitors, solitary figure in hospital gown.',
    captionTemplate: 'DSM-CHRONO WARD 7 // MARCUS WARD // OPTIC NERVE FIBROSIS CONFIRMED',
  },

  // --- VI. Existential Horror & Ledger ---
  synaptic_tear: {
    style: 'synaptic_tear',
    label: 'Chronognostic Synaptic Tearing',
    category: 'Existential Horror',
    accentColor: '#ff1744',
    secondaryColor: '#00f3ff',
    glowRgba: 'rgba(255, 23, 68, 0.35)',
    prompt: 'Haunting psychological dark sci-fi illustration of human neural pathways experiencing chronognostic aphasia, glowing synaptic lattice with violet and crimson micro-fractures.',
    captionTemplate: 'SYNAPTIC FRACTURE // RETINAL BLEED 40Hz // TEMPORAL APHASIA STAGE-III',
  },
  spectators_margin: {
    style: 'spectators_margin',
    label: 'Spectators in the Margin',
    category: 'Existential Horror',
    accentColor: '#aa00ff',
    secondaryColor: '#ff1744',
    glowRgba: 'rgba(170, 0, 255, 0.3)',
    prompt: 'Eerie cosmic horror depiction of vast, patient geometric silhouettes lurking inside the timeless stillness between Planck moments, non-Euclidean angles watching crew, 8k.',
    captionTemplate: 'OBSERVERS DETECTED // ZERO RESPIRATION // THE GAP WATCHES UNBLINKING',
  },
  cosmic_ledger: {
    style: 'cosmic_ledger',
    label: 'Vitrified Ledger Tablet',
    category: 'Existential Horror',
    accentColor: '#ffd600',
    secondaryColor: '#ff3d00',
    glowRgba: 'rgba(255, 214, 0, 0.3)',
    prompt: 'Alien cosmic ledger data matrix carved into vitrified black glass, glowing amber coordinates and mass reconciliation integrals, ominous mathematical architecture, 8k.',
    captionTemplate: 'COSMIC LEDGER AUDIT // MASS BALANCE: 0.042kg VARIANCE // GARBAGE COLLECTION',
  },
  recrystallization_shear: {
    style: 'recrystallization_shear',
    label: 'Re-Crystallization Shear Shock',
    category: 'Existential Horror',
    accentColor: '#ff3d00',
    secondaryColor: '#00f3ff',
    glowRgba: 'rgba(255, 61, 0, 0.3)',
    prompt: 'Violent physical reality re-crystallization when ship snaps back into physical space, atoms locking violently, atmospheric shockwaves, titanium micro-fractures, 8k.',
    captionTemplate: 'MOMENT RE-IGNITION // RE-CRYSTALLIZATION SHEAR // ACOUSTIC VIOLENCE',
  },
  mental_tearing: {
    style: 'mental_tearing',
    label: 'Synaptic Decay Spectrum',
    category: 'Existential Horror',
    accentColor: '#ff1744',
    secondaryColor: '#7c4dff',
    glowRgba: 'rgba(255, 23, 68, 0.3)',
    prompt: 'Terrifying neuro-anatomical split vision, chromatic red and cyan glitch, existential dread.',
    captionTemplate: 'DOUBLE VISION SCAN // NEURAL COORDINATES UNSYNCHRONIZED',
  },
  synaptic_decay: {
    style: 'synaptic_decay',
    label: 'Neuro-Lattice Atrophy',
    category: 'Existential Horror',
    accentColor: '#ff1744',
    secondaryColor: '#00e5ff',
    glowRgba: 'rgba(255, 23, 68, 0.28)',
    prompt: 'Medical sci-fi brainwave scan displaying severe chronognostic neural decay.',
    captionTemplate: 'CHRONO DECAY INDEX: 89.4% // MEMORY DRIFT DETECTED',
  },
  entity_margin: {
    style: 'entity_margin',
    label: 'Silhouettes in Non-Space',
    category: 'Existential Horror',
    accentColor: '#d500f9',
    secondaryColor: '#ff1744',
    glowRgba: 'rgba(213, 0, 249, 0.28)',
    prompt: 'Geometric silhouettes observing the ship from the silent seam between seconds.',
    captionTemplate: 'SILENT SPECTATORS // SPATIAL ANGLE: NON-EUCLIDEAN',
  },
  ledger_tablet: {
    style: 'ledger_tablet',
    label: 'Spatiotemporal Coordinate Ledger',
    category: 'Existential Horror',
    accentColor: '#ffb700',
    secondaryColor: '#ff1744',
    glowRgba: 'rgba(255, 183, 0, 0.28)',
    prompt: 'Black obsidian tablet with glowing coordinate equations.',
    captionTemplate: 'LEDGER TABLET // REGISTER VERIFIED // ERROR: 0',
  },
};

export function getAllArtworkStyles(): StyleMeta[] {
  return Object.values(STYLE_REGISTRY);
}

export function getArtworkStyleMeta(style: ArtworkStyle): StyleMeta {
  return STYLE_REGISTRY[style] || STYLE_REGISTRY.vast_spaces;
}

export function normalizeArtworkStyle(rawStyle: string): ArtworkStyle {
  if (rawStyle in STYLE_REGISTRY) {
    return rawStyle as ArtworkStyle;
  }
  return 'vast_spaces';
}

/**
 * Procedural Vector Synthesizer
 * Generates thematic, high-contrast, atmospheric SVG graphics for any style.
 */
export function generateProceduralSvg(options: {
  pageNumber: number;
  style: ArtworkStyle;
  title: string;
  seed?: number;
}): string {
  const { pageNumber } = options;
  const meta = getArtworkStyleMeta(options.style);
  const p = pageNumber;
  const seed = options.seed ?? p * 1337;

  const rand = (offset: number) => {
    const x = Math.sin(seed + offset) * 10000;
    return x - Math.floor(x);
  };

  const c1 = meta.accentColor;
  const c2 = meta.secondaryColor;

  // Custom SVGs based on category
  switch (meta.category) {
    case 'Cosmic & Void': {
      const cx = 200 + Math.round((rand(1) - 0.5) * 50);
      const cy = 110 + Math.round((rand(2) - 0.5) * 30);
      const radius = 55 + Math.round(rand(3) * 20);

      const stars = Array.from({ length: 36 }, (_, i) => {
        const sx = Math.round(rand(i * 3 + 10) * 400);
        const sy = Math.round(rand(i * 3 + 11) * 240);
        const r = (rand(i * 3 + 12) * 1.5 + 0.4).toFixed(1);
        const op = (rand(i * 3 + 13) * 0.7 + 0.25).toFixed(2);
        return `<circle cx="${sx}" cy="${sy}" r="${r}" fill="#fff" opacity="${op}"/>`;
      }).join('');

      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <radialGradient id="cosmic_bg_${p}" cx="50%" cy="50%" r="75%">
      <stop offset="0%" stop-color="#100820"/>
      <stop offset="50%" stop-color="#050612"/>
      <stop offset="100%" stop-color="#010206"/>
    </radialGradient>
    <radialGradient id="flare_${p}" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="1"/>
      <stop offset="30%" stop-color="${c1}" stop-opacity="0.9"/>
      <stop offset="70%" stop-color="${c2}" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="transparent"/>
    </radialGradient>
  </defs>
  <rect width="400" height="240" fill="url(#cosmic_bg_${p})"/>
  ${stars}
  <!-- Planetary Horizon Arc -->
  <path d="M -50 260 Q 200 120 450 260" fill="none" stroke="${c2}" stroke-width="2.5" opacity="0.6"/>
  <path d="M -50 263 Q 200 123 450 263" fill="none" stroke="${c1}" stroke-width="8" opacity="0.15"/>
  <!-- Gravitational Flare Center -->
  <circle cx="${cx}" cy="${cy}" r="${radius}" fill="url(#flare_${p})"/>
  <circle cx="${cx}" cy="${cy}" r="6" fill="#ffffff"/>
  <line x1="${cx - 140}" y1="${cy}" x2="${cx + 140}" y2="${cy}" stroke="${c1}" stroke-width="1.2" opacity="0.8"/>
  <line x1="${cx}" y1="${cy - 70}" x2="${cx}" y2="${cy + 70}" stroke="${c2}" stroke-width="0.8" opacity="0.5"/>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.9">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">SCENE #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }

    case 'Stations & Megastructures': {
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <linearGradient id="cloud_sky_${p}" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#0b1724"/>
      <stop offset="50%" stop-color="#0a2332"/>
      <stop offset="100%" stop-color="#08383f"/>
    </linearGradient>
  </defs>
  <rect width="400" height="240" fill="url(#cloud_sky_${p})"/>
  <!-- Distant Gas Giant Moon -->
  <circle cx="310" cy="50" r="32" fill="#061c28" stroke="${c2}" stroke-width="1" opacity="0.7"/>
  <!-- Cloud Ocean Bands -->
  <ellipse cx="200" cy="210" rx="260" ry="45" fill="#0d4752" opacity="0.8"/>
  <ellipse cx="120" cy="190" rx="180" ry="35" fill="#135b68" opacity="0.6"/>
  <ellipse cx="280" cy="175" rx="190" ry="30" fill="#1b7282" opacity="0.5"/>
  <!-- Station Gantry & Golden Observation Domes -->
  <g transform="translate(140, 95)">
    <rect x="-80" y="0" width="160" height="22" rx="3" fill="#0c1822" stroke="${c2}" stroke-width="0.8"/>
    <!-- Large Golden Observation Cupola Dome -->
    <circle cx="-35" cy="11" r="14" fill="${c1}" stroke="#ffffff" stroke-width="1.2" opacity="0.95"/>
    <circle cx="-35" cy="11" r="8" fill="#ffe082"/>
    <!-- Secondary Domes -->
    <circle cx="15" cy="11" r="9" fill="${c1}" opacity="0.85"/>
    <circle cx="45" cy="11" r="7" fill="${c1}" opacity="0.75"/>
    <!-- Lower Spire / Tether -->
    <line x1="-35" y1="22" x2="-35" y2="75" stroke="#1c2f40" stroke-width="4"/>
    <line x1="30" y1="22" x2="30" y2="60" stroke="#1c2f40" stroke-width="2"/>
  </g>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.9">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">HABITAT UNIT #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }

    case 'Ships & Fleets': {
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <radialGradient id="solar_sun_${p}" cx="75%" cy="35%" r="60%">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="35%" stop-color="${c1}"/>
      <stop offset="70%" stop-color="${c2}"/>
      <stop offset="100%" stop-color="#020305"/>
    </radialGradient>
  </defs>
  <rect width="400" height="240" fill="#03060a"/>
  <!-- Blazing Solar Disc in Backdrop (Syd Mead Aesthetic) -->
  <circle cx="280" cy="85" r="95" fill="url(#solar_sun_${p})"/>
  <!-- Starship Keel & Fleet Elements -->
  <g transform="translate(130, 115) rotate(-5)">
    <!-- Main Titanium Carrier Hull -->
    <polygon points="-70,5 30,-10 90,8 60,25 -50,22" fill="#0a121a" stroke="${c1}" stroke-width="1.4"/>
    <!-- Glowing Solar Sensor Arrays -->
    <rect x="-35" y="0" width="50" height="12" fill="${c1}" opacity="0.85"/>
    <line x1="-35" y1="6" x2="15" y2="6" stroke="#000" stroke-width="1"/>
    <!-- Trailing Escort Cruisers -->
    <polygon points="-110,-25 -80,-30 -60,-22 -95,-18" fill="#0d1822" stroke="${c2}" stroke-width="1"/>
    <polygon points="-80,45 -55,42 -40,48 -65,52" fill="#0d1822" stroke="${c2}" stroke-width="1"/>
    <!-- Sensor Antenna -->
    <line x1="90" y1="8" x2="120" y2="8" stroke="${c1}" stroke-width="1"/>
    <circle cx="120" cy="8" r="2" fill="#ffffff"/>
  </g>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.9">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">FLEET LOG #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }

    case 'Interiors & Wells': {
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <linearGradient id="reactor_mist_${p}" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#020804"/>
      <stop offset="50%" stop-color="#051408"/>
      <stop offset="100%" stop-color="#010402"/>
    </linearGradient>
    <radialGradient id="phosphor_glow_${p}" cx="50%" cy="40%" r="55%">
      <stop offset="0%" stop-color="${c1}" stop-opacity="0.85"/>
      <stop offset="50%" stop-color="${c2}" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="transparent"/>
    </radialGradient>
  </defs>
  <rect width="400" height="240" fill="url(#reactor_mist_${p})"/>
  <!-- Bulkhead Frames in Deep Perspective -->
  <g stroke="#122c18" stroke-width="1.8">
    <polygon points="20,10 380,10 330,220 70,220" fill="none"/>
    <polygon points="60,35 340,35 300,195 100,195" fill="none"/>
    <polygon points="110,65 290,65 260,165 140,165" fill="none"/>
  </g>
  <!-- Reactor Portal Phosphor Light Burst -->
  <circle cx="200" cy="115" r="45" fill="url(#phosphor_glow_${p})"/>
  <rect x="185" y="95" width="30" height="40" rx="3" fill="#010602" stroke="${c1}" stroke-width="1.5"/>
  <!-- Dripping Cryo Conduits Along Ceiling -->
  <path d="M 40 25 Q 200 45 360 25" fill="none" stroke="${c1}" stroke-width="2.5" opacity="0.6"/>
  <path d="M 50 35 Q 200 55 350 35" fill="none" stroke="${c2}" stroke-width="1.5" opacity="0.5"/>
  <!-- Grated Titanium Decking -->
  <g stroke="${c1}" stroke-opacity="0.2" stroke-width="0.8">
    <line x1="70" y1="220" x2="140" y2="165"/>
    <line x1="130" y1="220" x2="170" y2="165"/>
    <line x1="200" y1="220" x2="200" y2="165"/>
    <line x1="270" y1="220" x2="230" y2="165"/>
    <line x1="330" y1="220" x2="260" y2="165"/>
  </g>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.9">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">ENGINEERING LOG #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }

    case 'Architecture & Transit': {
      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <linearGradient id="corridor_grad_${p}" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#18222a"/>
      <stop offset="60%" stop-color="#0c1218"/>
      <stop offset="100%" stop-color="#040608"/>
    </linearGradient>
    <linearGradient id="shaft_light_${p}" x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.7"/>
      <stop offset="60%" stop-color="${c2}" stop-opacity="0.2"/>
      <stop offset="100%" stop-color="transparent"/>
    </linearGradient>
  </defs>
  <rect width="400" height="240" fill="url(#corridor_grad_${p})"/>
  <!-- Volumetric Ceiling Light Shaft -->
  <polygon points="160,0 240,0 290,200 110,200" fill="url(#shaft_light_${p})"/>
  <!-- Structural Ribs & Tunnel Walls (Syd Mead Concept Sketch) -->
  <g stroke="#3a4d5e" stroke-width="1.4">
    <line x1="0" y1="20" x2="160" y2="90"/>
    <line x1="400" y1="20" x2="240" y2="90"/>
    <line x1="0" y1="220" x2="160" y2="150"/>
    <line x1="400" y1="220" x2="240" y2="150"/>
    <!-- Receding Doorway Frames -->
    <rect x="160" y="90" width="80" height="60" rx="3" fill="#080c10" stroke="${c1}" stroke-width="1.2"/>
    <rect x="175" y="100" width="50" height="40" fill="#030507" stroke="#ffffff" stroke-width="0.8" opacity="0.6"/>
  </g>
  <!-- Orange / Rust Safety Warning Stencils -->
  <g fill="${c1}" opacity="0.85">
    <rect x="30" y="80" width="36" height="42" rx="2" fill="none" stroke="${c1}" stroke-width="1"/>
    <circle cx="48" cy="101" r="12" fill="none" stroke="${c1}" stroke-width="1.2"/>
    <text x="40" y="105" font-family="monospace" font-size="9" font-weight="bold">TV</text>
  </g>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.9">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">ARCHITECTURAL SKETCH #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }

    case 'Existential Horror':
    default: {
      const cx = 200;
      const cy = 110;

      return `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 240" width="100%" height="100%">
  <defs>
    <radialGradient id="horror_bg_${p}" cx="50%" cy="50%" r="75%">
      <stop offset="0%" stop-color="#1f040d"/>
      <stop offset="60%" stop-color="#0a0205"/>
      <stop offset="100%" stop-color="#020002"/>
    </radialGradient>
  </defs>
  <rect width="400" height="240" fill="url(#horror_bg_${p})"/>
  <!-- Retinal Bleed Coordinate Grid -->
  <g stroke="${c1}" stroke-opacity="0.12" stroke-width="0.75">
    ${Array.from({ length: 8 }, (_, i) => `<line x1="0" y1="${i * 30}" x2="400" y2="${i * 30}"/>`).join('')}
    ${Array.from({ length: 10 }, (_, i) => `<line x1="${i * 40}" y1="0" x2="${i * 40}" y2="240"/>`).join('')}
  </g>
  <!-- Fractured Neural Web with Chromatic Double Vision -->
  <g stroke="${c2}" stroke-width="1.2" opacity="0.6" transform="translate(-4, -2)">
    <line x1="${cx}" y1="${cy}" x2="110" y2="55"/>
    <line x1="${cx}" y1="${cy}" x2="290" y2="50"/>
    <line x1="${cx}" y1="${cy}" x2="100" y2="165"/>
    <line x1="${cx}" y1="${cy}" x2="300" y2="165"/>
    <circle cx="110" cy="55" r="5" fill="${c2}"/>
    <circle cx="290" cy="50" r="5" fill="${c2}"/>
  </g>
  <g stroke="${c1}" stroke-width="1.6" opacity="0.9" transform="translate(3, 3)">
    <line x1="${cx}" y1="${cy}" x2="110" y2="55"/>
    <line x1="${cx}" y1="${cy}" x2="290" y2="50"/>
    <line x1="${cx}" y1="${cy}" x2="100" y2="165"/>
    <line x1="${cx}" y1="${cy}" x2="300" y2="165"/>
    <circle cx="${cx}" cy="${cy}" r="14" fill="none" stroke="${c1}" stroke-width="2"/>
    <circle cx="110" cy="55" r="6" fill="${c1}"/>
    <circle cx="290" cy="50" r="6" fill="${c1}"/>
    <circle cx="100" cy="165" r="6" fill="${c1}"/>
    <circle cx="300" cy="165" r="6" fill="${c1}"/>
  </g>
  <!-- EEG Erratic Brainwave / Quantum Jitter Line -->
  <polyline points="20,185 80,185 95,160 105,205 115,185 180,185 195,150 205,215 215,185 300,185 315,170 325,195 380,185" fill="none" stroke="${c1}" stroke-width="1.4"/>
  <!-- Telemetry Bar -->
  <g font-family="monospace" font-size="8.5" fill="${c1}" opacity="0.95">
    <text x="18" y="214">${meta.captionTemplate}</text>
    <text x="18" y="226" fill="#6b7c96" font-size="7.5">CHRONO-PATHOLOGY #${p.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}</text>
  </g>
</svg>
`)}`;
    }
  }
}

/**
 * Assigns high-resolution photorealistic concept art paintings across all 1,000 pages.
 */
export const STYLE_CONCEPT_PAINTINGS: Record<string, string> = {
  quantum_schematic: '/art/quantum_core_explosion.jpg',
  particulate_decay: '/art/synaptic_decay_chamber.jpg',
  planck_shear: '/art/caesura_jump_gate.jpg',
  temporal_freeze: '/art/chapter11_stillness_breathes.jpg',
  chromatic_break: '/art/planck_ignition_event.jpg',
  cosmic_flare: '/art/planck_ignition_event.jpg',
  event_horizon: '/art/chapter5_null_rift.jpg',
  stellar_nursery: '/art/non_euclidean_void_chasm.jpg',
  unrendered_void: '/art/obsidian_ledger_void.jpg',
  vast_spaces: '/art/chapter1_tick_and_void.jpg',
  deep_quarantine: '/art/perseus_terminal_citadel.jpg',
  orbital_gantry: '/art/colossal_orbital_ring.jpg',
  busy_stations: '/art/asteroid_smelting_forge.jpg',
  perseus_terminal: '/art/perseus_terminal_citadel.jpg',
  cloud_station: '/art/colossal_orbital_ring.jpg',
  solitary_hauler: '/art/derelict_ghost_freighter.jpg',
  lonely_ships: '/art/keel_ussc_geryon.jpg',
  solar_armada: '/art/keel_ussc_geryon.jpg',
  solar_fleet: '/art/keel_ussc_geryon.jpg',
  combat_corvette: '/art/caesura_jump_gate.jpg',
  cryogenic_hull: '/art/cryo_stasis_well_chamber.jpg',
  phosphor_spine: '/art/quantum_core_explosion.jpg',
  cryostat_choke: '/art/cryo_stasis_well_chamber.jpg',
  stasis_vaults: '/art/stasis_pod_awakening.jpg',
  cargo_void: '/art/derelict_ghost_freighter.jpg',
  transit_tube: '/art/perseus_terminal_citadel.jpg',
  transit_corridor: '/art/perseus_terminal_citadel.jpg',
  tribunal_hall: '/art/tribunal_inquisition_hall.jpg',
  corporate_tribunal: '/art/tribunal_inquisition_hall.jpg',
  hydroponics: '/art/cryo_stasis_well_chamber.jpg',
  quarantine_brig: '/art/tribunal_inquisition_hall.jpg',
  synaptic_tear: '/art/synaptic_decay_chamber.jpg',
  mental_tearing: '/art/synaptic_decay_chamber.jpg',
  obsidian_ledger: '/art/obsidian_ledger_void.jpg',
  margin_entity: '/art/spectator_margin_entity.jpg',
  spectators_margin: '/art/spectator_margin_entity.jpg',
  entity_margin: '/art/spectator_margin_entity.jpg',
  cosmic_ledger: '/art/obsidian_ledger_void.jpg',
  ledger_tablet: '/art/obsidian_ledger_void.jpg',
  recrystallization_shear: '/art/planck_ignition_event.jpg',
  chrono_psych_ward: '/art/synaptic_decay_chamber.jpg',
};

export function getHeroImageForPage(pageNum: number, style: ArtworkStyle): string {
  // Act-specific narrative milestone paintings for 452-page epic
  if (pageNum <= 7) return '/art/caesura_jump_gate.jpg';
  if (pageNum >= 8 && pageNum <= 45) {
    if (pageNum === 8 || pageNum === 9) return '/art/marcus_ward_portrait.jpg';
    if (pageNum >= 10 && pageNum <= 25) return '/art/chapter1_tick_and_void.jpg';
    return '/art/keel_ussc_geryon.jpg';
  }
  if (pageNum >= 46 && pageNum <= 93) {
    if (pageNum >= 46 && pageNum <= 65) return '/art/chapter5_null_rift.jpg';
    return '/art/derelict_ghost_freighter.jpg';
  }
  if (pageNum >= 94 && pageNum <= 151) {
    if (pageNum >= 94 && pageNum <= 120) return '/art/quantum_core_explosion.jpg';
    return '/art/chapter11_stillness_breathes.jpg';
  }
  if (pageNum >= 152 && pageNum <= 205) {
    if (pageNum >= 152 && pageNum <= 175) return '/art/spectator_margin_entity.jpg';
    return '/art/non_euclidean_void_chasm.jpg';
  }
  if (pageNum >= 206 && pageNum <= 247) {
    if (pageNum >= 206 && pageNum <= 225) return '/art/dr_simon_peter_portrait.jpg';
    return '/art/synaptic_decay_chamber.jpg';
  }
  if (pageNum >= 248 && pageNum <= 293) {
    if (pageNum >= 248 && pageNum <= 270) return '/art/elena_cruz_portrait.jpg';
    return '/art/obsidian_ledger_void.jpg';
  }
  if (pageNum >= 294 && pageNum <= 333) {
    return '/art/cryo_stasis_well_chamber.jpg';
  }
  if (pageNum >= 334 && pageNum <= 369) {
    return '/art/planck_ignition_event.jpg';
  }
  if (pageNum >= 370 && pageNum <= 401) {
    return '/art/derelict_ghost_freighter.jpg';
  }
  if (pageNum >= 402 && pageNum <= 428) {
    if (pageNum >= 402 && pageNum <= 415) return '/art/inquisitor_thorne_portrait.jpg';
    return '/art/tribunal_inquisition_hall.jpg';
  }
  // Appendices (429 - 452)
  if (pageNum === 452) return '/art/planck_ignition_event.jpg';
  return STYLE_CONCEPT_PAINTINGS[style] || '/art/perseus_terminal_citadel.jpg';
}

export function buildArtworkCue(
  pageNumber: number,
  style: ArtworkStyle,
  title: string
): ArtworkCue {
  const normStyle = normalizeArtworkStyle(style);
  const meta = getArtworkStyleMeta(normStyle);

  return {
    id: `art_${pageNumber.toString().padStart(4, '0')}`,
    title: `Visual Telemetry: ${title}`,
    prompt: meta.prompt,
    style: normStyle,
    caption: `Sensor Telemetry #${pageNumber.toString().padStart(4, '0')} // ${meta.label.toUpperCase()}`,
    svgDataUri: generateProceduralSvg({ pageNumber, style: normStyle, title }),
    generatedImageUrl: getHeroImageForPage(pageNumber, normStyle),
  };
}
